import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { franc } from 'franc'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../contexts/AuthContext'
import { Book, CreateBookData, apiService } from '../services/api'

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  draft: { backgroundColor: 'var(--bg-hover)', color: 'var(--text-secondary)' },
  in_progress: { backgroundColor: 'rgba(25, 118, 210, 0.16)', color: '#5da7df' },
  review: { backgroundColor: 'rgba(237, 108, 2, 0.16)', color: '#f0a24b' },
  published: { backgroundColor: 'rgba(46, 125, 50, 0.16)', color: '#79c27f' },
  archived: { backgroundColor: 'rgba(211, 47, 47, 0.16)', color: '#ef7777' },
}

const styles = {
  page: { backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' },
  card: { backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' },
  control: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border-primary)',
    color: 'var(--text-primary)',
  },
  menu: { backgroundColor: 'var(--menu-bg)', border: '1px solid var(--menu-border)', color: 'var(--text-primary)' },
  chip: { backgroundColor: 'var(--bg-hover)', color: 'var(--text-secondary)' },
  primaryText: { color: 'var(--text-primary)' },
  secondaryText: { color: 'var(--text-secondary)' },
  icon: { color: 'var(--icon-default)' },
  dividerTop: { borderTop: '1px solid var(--border-subtle)' },
  spinner: { border: '4px solid var(--border-subtle)', borderTopColor: '#7cb8e4' },
} satisfies Record<string, React.CSSProperties>

// Book Card Component
const BookCard: React.FC<{
  book: Book
  onEdit: (book: Book) => void
  onDelete: (bookId: string) => void
  onOpen: (book: Book) => void
}> = ({ book, onEdit, onDelete, onOpen }) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div
      className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
      style={styles.card}
      onClick={() => onOpen(book)}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-1" style={styles.primaryText}>
              {book.title}
            </h3>

            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={STATUS_STYLES[book.status] || styles.chip}
            >
              {book.status.replace('_', ' ')}
            </span>
          </div>

          {/* Actions Dropdown */}
          <div className="relative">
            <button
              className="p-1"
              style={styles.icon}
              onClick={(e) => {
                e.stopPropagation()
                setShowDropdown(!showDropdown)
              }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowDropdown(false)
                  }}
                />

                <div className="absolute right-0 top-8 w-48 rounded-md shadow-lg z-20" style={styles.menu}>
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDropdown(false)
                        onEdit(book)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm"
                      style={styles.primaryText}
                    >
                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Details
                    </button>

                    <div style={styles.dividerTop}></div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDropdown(false)
                        onDelete(book._id)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm"
                      style={{ color: '#ef7777' }}
                    >
                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Language */}
        <div className="mb-4">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium" style={styles.chip}>
            Language: {book.metadata?.language || 'en'}
          </span>
        </div>

        {/* Footer */}
        <div className="pt-4" style={styles.dividerTop}>
          <div className="text-xs" style={styles.secondaryText}>
            Updated {formatDate(book.updatedAt)}
          </div>
        </div>
      </div>
    </div>
  )
}

// Book Modal Component (for both create and edit)
const BookModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateBookData) => void
  book?: Book | null // Optional book for edit mode
  mode: 'create' | 'edit'
}> = ({ isOpen, onClose, onSubmit, book, mode }) => {
  const [formData, setFormData] = useState<CreateBookData>({
    title: book?.title || '',
    language: book?.metadata?.language || 'en',
    visibility: book?.visibility || 'private',
  })
  const [titleError, setTitleError] = useState('')
  const [languageError, setLanguageError] = useState('')

  // Update form data when book prop changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && book) {
      setFormData({
        title: book.title,
        language: book.metadata?.language || 'en',
        visibility: book.visibility,
      })
    } else if (mode === 'create') {
      setFormData({
        title: '',
        language: 'en',
        visibility: 'private',
      })
    }
    setTitleError('')
    setLanguageError('')
  }, [book, mode])

  // Language detection helper
  const detectLanguage = (text: string) => {
    if (text.length < 2) {
      return 'en'
    } // Default to English for very short text

    try {
      // First try franc for statistical detection
      const detected = franc(text)

      // Convert franc codes to ISO 639-1 codes
      const languageMap: { [key: string]: string } = {
        eng: 'en',
        spa: 'es',
        fra: 'fr',
        deu: 'de',
        ita: 'it',
        por: 'pt',
        rus: 'ru',
        jpn: 'ja',
        kor: 'ko',
        cmn: 'zh',
        ara: 'ar',
        hin: 'hi',
        heb: 'he', // Hebrew
      }

      // If franc gave us a definitive result, use it
      if (detected !== 'und' && languageMap[detected]) {
        return languageMap[detected]
      }

      // Fallback: Check for specific character ranges when franc returns 'und'
      // Hebrew characters (including punctuation and cantillation marks)
      if (/[\u0590-\u05FF]/.test(text)) {
        return 'he'
      }

      // Arabic characters
      if (/[\u0600-\u06FF\u0750-\u077F]/.test(text)) {
        return 'ar'
      }

      // Cyrillic characters (Russian, etc.)
      if (/[\u0400-\u04FF]/.test(text)) {
        return 'ru'
      }

      // Chinese characters (Simplified/Traditional)
      if (/[\u4E00-\u9FFF]/.test(text)) {
        return 'zh'
      }

      // Japanese (Hiragana, Katakana)
      if (/[\u3040-\u309F\u30A0-\u30FF]/.test(text)) {
        return 'ja'
      }

      // Korean (Hangul)
      if (/[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/.test(text)) {
        return 'ko'
      }

      // Default to English if no specific script is detected
      return 'en'
    } catch {
      return 'en' // Default to English on error
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    let hasError = false

    if (!formData.title.trim()) {
      setTitleError('Please enter a book title')
      hasError = true
    } else {
      setTitleError('')
    }

    if (!formData.language) {
      setLanguageError('Please select a language')
      hasError = true
    } else {
      setLanguageError('')
    }

    if (hasError) {
      return
    }

    onSubmit(formData)
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value
    setFormData({
      ...formData,
      title,
      language: detectLanguage(title),
    })
    if (titleError && title.trim()) {
      setTitleError('')
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {mode === 'create' ? 'Create New Book' : 'Edit Book'}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.title}
            onChange={handleTitleChange}
            placeholder="Enter book title"
            required
            error={!!titleError}
            helperText={titleError || undefined}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Language *</InputLabel>
            <Select
              value={formData.language}
              label="Language *"
              onChange={(e) => {
                setFormData({ ...formData, language: e.target.value })
                if (languageError) {
                  setLanguageError('')
                }
              }}
              required
              error={!!languageError}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
              <MenuItem value="de">German</MenuItem>
              <MenuItem value="it">Italian</MenuItem>
              <MenuItem value="pt">Portuguese</MenuItem>
              <MenuItem value="ru">Russian</MenuItem>
              <MenuItem value="ja">Japanese</MenuItem>
              <MenuItem value="ko">Korean</MenuItem>
              <MenuItem value="zh">Chinese</MenuItem>
              <MenuItem value="ar">Arabic</MenuItem>
              <MenuItem value="hi">Hindi</MenuItem>
              <MenuItem value="he">Hebrew</MenuItem>
            </Select>
            <FormHelperText>{languageError || 'Language is auto-detected based on your title'}</FormHelperText>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Visibility</InputLabel>
            <Select
              value={formData.visibility}
              label="Visibility"
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value as any })}
            >
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'create' ? 'Create Book' : 'Update Book'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

// Main Dashboard Component
const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [books, setBooks] = useState<Book[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingBook, setEditingBook] = useState<Book | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Load books
  useEffect(() => {
    loadBooks()
  }, [])

  const loadBooks = async () => {
    try {
      const response = await apiService.getBooks()
      const firstBook = response.data?.[0]
      if (firstBook) {
        navigate(`/edit/${firstBook._id}`, { replace: true })
        return
      }
      setBooks(response.data)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load books')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateBook = async (bookData: CreateBookData) => {
    try {
      const response = await apiService.createBook(bookData)
      setBooks([response.data, ...books])
      setIsCreateModalOpen(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to create book')
    }
  }

  const handleEditBook = (book: Book) => {
    setEditingBook(book)
    setIsEditModalOpen(true)
  }

  const handleUpdateBook = async (bookData: CreateBookData) => {
    if (!editingBook) {
      return
    }

    try {
      const response = await apiService.updateBook(editingBook._id, bookData)
      setBooks(books.map((book) => (book._id === editingBook._id ? response.data : book)))
      setIsEditModalOpen(false)
      setEditingBook(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to update book')
    }
  }

  const handleDeleteBook = async (bookId: string) => {
    if (!window.confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return
    }

    try {
      await apiService.deleteBook(bookId)
      setBooks(books.filter((book) => book._id !== bookId))
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete book')
    }
  }

  const handleOpenBook = (book: Book) => {
    // Navigate to book editor
    window.location.href = `/edit/${book._id}`
  }

  // Filter books
  const filteredBooks = books.filter((book) => {
    const matchesSearch =
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.metadata?.language?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || book.status === statusFilter
    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={styles.page}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 mx-auto" style={styles.spinner}></div>
          <p className="mt-4" style={styles.secondaryText}>
            Loading your books...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={styles.page}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={styles.primaryText}>
            Welcome back, {user?.username}!
          </h1>
          <p style={styles.secondaryText}>Manage your books and continue your writing journey.</p>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search books..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
                style={styles.control}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5"
                style={styles.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              style={styles.control}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="review">Review</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {books.length === 0 && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Book
            </button>
          )}
        </div>

        {/* Books Grid */}
        {filteredBooks.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12"
              style={styles.icon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium" style={styles.primaryText}>
              No books found
            </h3>
            <p className="mt-1 text-sm" style={styles.secondaryText}>
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first book.'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <div className="mt-6">
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Create your first book
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBooks.map((book) => (
              <BookCard
                key={book._id}
                book={book}
                onEdit={handleEditBook}
                onDelete={handleDeleteBook}
                onOpen={handleOpenBook}
              />
            ))}
          </div>
        )}

        {/* Create Book Modal */}
        <BookModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateBook}
          mode="create"
        />

        {/* Edit Book Modal */}
        <BookModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setEditingBook(null)
          }}
          onSubmit={handleUpdateBook}
          book={editingBook}
          mode="edit"
        />
      </div>
    </div>
  )
}

export default Dashboard
